import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useAppData from 'app/services/hooks/useAppData'
import useLogger from 'lib/util/useLogger'
import getAvatarBases from './actions/getAvatarBases'
import getBrands from './actions/getBrands'
import getBuckets from './actions/getBuckets'
import getElements from './actions/getElements'
import getPrices from './actions/getPrices'
import getSupply from './actions/getSupply'
import getUtility from './actions/getUtility'
import parsePersonaData from './actions/parsePersonaData'
import useMaticToUSD from './useMATICToUSD'

const debug = useLogger('Persona Provider', 'lightblue')
const STORE_CACHE_DURATION = 60000 * 60 // 1 hour

/**
 * The `usePersona`
 *
 * @returns {object} the context
 */
function usePersona () {
  const appData = useAppData()
  const [storeData, setStoreData] = useState(null)
  const cachedData = useRef(null)
  const maticToUsdMultiplier = useMaticToUSD()

  /**
   * Load the data
   */
  const loadStoreData = useCallback(async () => {
    const now = Date.now()

    if (
      cachedData.current &&
      cachedData.current.timestamp - now < STORE_CACHE_DURATION
    ) {
      // If store data has previously loaded and we are in
      // acceptable cache duration, only refresh the live
      // data (prices/supply)
      return refreshLiveStoreData()
    }

    try {
      const [
        brandsRes,
        brandBucketsRes,
        basesRes,
        elementsRes,
        supplyRes,
        pricesRes
      ] = await Promise.all([
        getBrands(),
        getBuckets(),
        getAvatarBases(),
        getElements(),
        getSupply(),
        getPrices()
      ])

      const allResponsesSuccessful = [
        brandsRes,
        brandBucketsRes,
        basesRes,
        elementsRes,
        supplyRes,
        pricesRes
      ].every(data => data.success)

      if (!allResponsesSuccessful) {
        debug.error(
          'Store load failed',
          brandsRes,
          brandBucketsRes,
          basesRes,
          elementsRes,
          supplyRes,
          pricesRes
        )

        return { success: false }
      }

      // supplyRes.data[372] = 0
      // supplyRes.data[379] = 0

      const data = {
        timestamp: now,
        categoryConfig: appData?.category_config || {},
        utilityConfig: appData?.utilities || [],
        bases: basesRes.data,
        elements: elementsRes.data,
        brands: brandsRes.data,
        brandBuckets: brandBucketsRes.data,
        content: appData
      }

      const parsedStoreData = parsePersonaData({
        ...data,
        supply: supplyRes.data,
        prices: pricesRes.data
      })

      // Update cached data, post parsing (safe)
      cachedData.current = data

      setStoreData(parsedStoreData)

      debug.log('Store load success', parsedStoreData)

      return {
        success: true,
        data: parsedStoreData
      }
    } catch (error) {
      debug.error('Store load failed', error)

      return { success: false }
    }
  }, [appData])

  /**
   * Refresh the live store data (price & supply)
   */
  const refreshLiveStoreData = useCallback(async () => {
    const [supplyRes, pricesRes] = await Promise.all([
      getSupply(),
      getPrices()
    ])
    const allResponsesSuccessful = [supplyRes, pricesRes].every(
      data => data.success
    )

    if (!allResponsesSuccessful) {
      debug.error('Store refresh failed')

      return { success: false }
    }

    // Debug - Fake a supply removal
    // supplyRes.data[26] = 0

    const parsedStoreData = parsePersonaData({
      ...cachedData.current,
      supply: supplyRes.data,
      prices: pricesRes.data
    })

    debug.log('Store refreshed', parsedStoreData)

    setStoreData(parsedStoreData)

    return {
      success: true,
      data: parsedStoreData
    }
  }, [])

  /**
   * Load product utility - either a single item or an array
   */
  const loadProductUtility = useCallback(
    async productId => {
      const res = await getUtility(productId, appData?.sub_utilities || {})

      return res
    },
    [appData]
  )

  /**
   * Verify the supply - used to get the latest supply information
   * and verify the supplied items by ID against the latest
   * supply
   *
   * @param {Array} elementIds - the array of elements by ID
   * @returns {Promise} resolves on completion
   */
  const verifySupply = useCallback(async elementIds => {
    const refreshedStore = await refreshLiveStoreData()

    if (!refreshedStore.success) {
      return {
        success: false,
        message: 'There was a problem verifying the supply. Please try again.',
        outOfStock: []
      }
    }

    // Look through all the element ID's and find any
    // that either can't be matched (edge case) or that do not
    // have supply. When the supply is zero, we know the item
    // is no longer in stock!
    const outOfStock = elementIds.reduce((res, id) => {
      const { supply } = refreshedStore.data.elements[id] || {}

      if (!supply) {
        // Out of stock!
        res.push(id)
      }

      return res
    }, [])

    return {
      success: !outOfStock.length,
      outOfStock
    }
  }, [])

  /**
   * Effect: On receipt of app data, load the store
   */
  useEffect(() => {
    if (!appData) {
      return
    }

    loadStoreData()
  }, [appData])

  /**
   * Memo: The context object
   */
  const ctx = useMemo(
    () => ({
      storeData,
      loadStoreData,
      loadProductUtility,
      maticToUsdMultiplier,
      verifySupply
    }),
    [
      storeData,
      loadStoreData,
      loadProductUtility,
      maticToUsdMultiplier,
      verifySupply
    ]
  )

  return ctx
}

// Export
export default usePersona
